import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { SyncLogRow } from '@/lib/supabase-to-sem';

export interface SyncStatus {
  lastSyncedAt: Date | null;
  status: 'success' | 'partial' | 'failed' | 'running' | 'unknown';
  accountsSynced: number;
  accountsTotal: number;
  hoursAgo: number | null;
  errorMessage: string | null;
}

export function useSyncStatus(): SyncStatus {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    lastSyncedAt: null,
    status: 'unknown',
    accountsSynced: 0,
    accountsTotal: 10,
    hoursAgo: null,
    errorMessage: null,
  });

  useEffect(() => {
    const fetchStatus = async () => {
      if (!supabase) return;

      // Get the most recent sync entry
      const { data: latest } = await supabase
        .from('sync_log')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(1);

      if (!latest || latest.length === 0) return;

      const latestRow = latest[0] as SyncLogRow;

      // If the latest sync failed, check if there's a recent successful one
      // (quota errors don't invalidate existing data)
      if (latestRow.status === 'failed') {
        const { data: lastGood } = await supabase
          .from('sync_log')
          .select('*')
          .in('status', ['success', 'partial'])
          .order('started_at', { ascending: false })
          .limit(1);

        if (lastGood && lastGood.length > 0) {
          const goodRow = lastGood[0] as SyncLogRow;
          const completedAt = goodRow.completed_at ? new Date(goodRow.completed_at) : null;
          const hoursAgo = completedAt
            ? Math.round((Date.now() - completedAt.getTime()) / (1000 * 60 * 60))
            : null;

          setSyncStatus({
            lastSyncedAt: completedAt,
            status: goodRow.status as SyncStatus['status'],
            accountsSynced: goodRow.accounts_synced,
            accountsTotal: goodRow.accounts_total,
            hoursAgo,
            errorMessage: null,
          });
          return;
        }
      }

      // Use the latest row as-is
      const completedAt = latestRow.completed_at ? new Date(latestRow.completed_at) : null;
      const hoursAgo = completedAt
        ? Math.round((Date.now() - completedAt.getTime()) / (1000 * 60 * 60))
        : null;

      setSyncStatus({
        lastSyncedAt: completedAt,
        status: latestRow.status as SyncStatus['status'],
        accountsSynced: latestRow.accounts_synced,
        accountsTotal: latestRow.accounts_total,
        hoursAgo,
        errorMessage: latestRow.error_message,
      });
    };

    fetchStatus();
  }, []);

  return syncStatus;
}
